import { motion } from 'framer-motion';
import { 
  MapPin, ArrowRight, CheckCircle, Clock,
  Navigation, Users, TrendingUp, Smartphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const features = [
  { icon: Navigation, title: "Detection automatique", text: "Le systeme detecte quand tu arrives sur le chantier" },
  { icon: Clock, title: "Temps enregistre", text: "Le temps de travail est enregistre automatiquement" },
  { icon: TrendingUp, title: "Rentabilite calculee", text: "Connaissez la rentabilite reelle de chaque chantier" },
  { icon: Users, title: "Suivi des équipes", text: "Suis le temps de tes salariés et équipes" },
];

const benefits = [
  "Connaitre le temps reel passe sur chaque chantier",
  "Calculer la rentabilite avec precision",
  "Suivre les equipes et les salaries",
  "Optimiser la planification",
];

export default function GeolocalisationPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Pointage GPS | Suivi Temps & Rentabilité Chantiers - BatiAssist"
        description="Pointage automatique par géolocalisation pour artisans : détection d'arrivée sur chantier, calcul du temps réel, analyse de rentabilité et suivi des équipes."
        keywords="pointage GPS chantier, géolocalisation artisan, suivi temps BTP, rentabilité chantier, pointage automatique"
        path="/fonctionnalites/geolocalisation"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-success/10 text-ba-success text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              Geolocalisation
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Suivez automatiquement le temps{' '}
              <span className="text-ba-secondary">passe sur tes chantiers</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Plus besoin de noter tes heures. Le système détecte automatiquement 
              quand tu arrives et quand tu pars.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Optimiser mes chantiers
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Comment ca fonctionne ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, title: 'Arrivee sur chantier', desc: 'Le systeme detecte automatiquement ta position GPS et demarre le chrono', icon: Navigation },
              { step: 2, title: 'Travail sur site', desc: 'Le temps est enregistre en arriere-plan pendant que tu travailles', icon: Clock },
              { step: 3, title: 'Depart du chantier', desc: 'Quand tu pars, le temps est sauvegarde automatiquement', icon: CheckCircle },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="text-center bg-slate-50 rounded-2xl p-8"
                >
                  <div className="w-16 h-16 bg-ba-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="w-8 h-8 bg-ba-secondary rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3 text-lg">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8">
                Tout ce que tu peux faire
              </h2>
              <div className="space-y-6">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-ba-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-ba-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800 mb-1">{feature.title}</h3>
                        <p className="text-slate-600">{feature.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-ba-success to-emerald-600 rounded-3xl p-8"
            >
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-ba-success/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-ba-success" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Chantier Famille Dupont</p>
                    <p className="text-sm text-slate-500">12 rue des Lilas, Paris</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-ba-primary">6h 45min</p>
                    <p className="text-sm text-slate-500">Temps total</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-ba-success">85%</p>
                    <p className="text-sm text-slate-500">Rentabilite</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Lundi 11 mars</span>
                    <span className="font-medium">3h 30min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Mardi 12 mars</span>
                    <span className="font-medium">3h 15min</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team tracking */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="font-bold text-slate-800 mb-4">Suivi des equipes</h3>
                <div className="space-y-4">
                  {[
                    { name: 'Marc Dupont', chantier: 'Villa Garcia', time: '4h 20min', status: 'Sur site' },
                    { name: 'Jean Martin', chantier: 'Maison Leroy', time: '3h 45min', status: 'Sur site' },
                    { name: 'Pierre Bernard', chantier: 'Appt. Rossi', time: '2h 30min', status: 'Termine' },
                  ].map((member, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-ba-primary/10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-ba-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.chantier}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate-800">{member.time}</p>
                        <p className={`text-xs ${member.status === 'Sur site' ? 'text-ba-success' : 'text-slate-400'}`}>
                          {member.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Suis aussi tes salariés et équipes
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Le système permet également de suivre le temps de travail de tes équipes. 
                Chaque salarie peut utiliser l'application sur son telephone.
              </p>
              <div className="flex items-center gap-3 text-ba-success">
                <Smartphone className="w-5 h-5" />
                <span className="font-medium">Application mobile incluse</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Les avantages pour ton entreprise
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200"
              >
                <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                <span className="text-slate-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-ba-success">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Optimisez la rentabilite de tes chantiers
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Arretez de deviner le temps passe. Mesurez-le automatiquement.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-ba-success font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Optimiser mes chantiers
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
